import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "i18next";

import { ReactComponent as ChatIcon } from '../assets/icons/svg/auth/chat.svg';
import { ReactComponent as KoFlag } from '../assets/icons/svg/auth/ko.svg';
import { ReactComponent as EnFlag } from '../assets/icons/svg/auth/en.svg';
import { ReactComponent as DownArrow } from '../assets/icons/svg/auth/down.svg';

const languages = [
    { code: "ko", nameKey: "auth.language.ko", Icon: KoFlag },
    { code: "en", nameKey: "auth.language.en", Icon: EnFlag }
];

const AuthPageSkeleton = () => (
    <div className="w-full min-h-[100dvh] bg-white flex flex-col items-center overflow-x-hidden" aria-hidden="true">
        <div className="grid grid-cols-4 gap-1 md:grid-cols-12 md:gap-2 w-full px-6 h-full pt-safe-top mx-auto max-w-[640px] lg:max-w-[944px]">
            <div className="col-span-4 md:col-start-4 md:col-span-6 flex flex-col items-start flex-1 pt-[56px] md:pt-[96px]">
                <div className="w-[56px] h-[56px] rounded-2xl bg-[#F0F1F2] animate-pulse mb-2" />
                <div className="flex flex-col gap-1 self-stretch mb-8">
                    <div className="h-[28px] w-[220px] rounded bg-[#F0F1F2] animate-pulse" />
                    <div className="h-[28px] w-[180px] rounded bg-[#F0F1F2] animate-pulse" />
                </div>
                <div className="w-full h-12 rounded-lg bg-[#F0F1F2] animate-pulse mb-2" />
                <div className="w-full h-12 rounded-lg bg-[#F0F1F2] animate-pulse mb-2" />
                <div className="w-full flex justify-center">
                    <div className="flex items-center gap-2 p-2">
                        <div className="w-6 h-6 rounded-full bg-[#F0F1F2] animate-pulse" />
                        <div className="w-12 h-4 rounded bg-[#F0F1F2] animate-pulse" />
                        <div className="w-6 h-6 rounded-md bg-[#F0F1F2] animate-pulse" />
                    </div>
                </div>
            </div>
            <div className="w-full h-safe-bottom shrink-0 mt-auto col-span-full" />
        </div>
    </div>
);

const AuthPage = () => {
    const { t } = useTranslation();
    const baseUrl = process.env.REACT_APP_API_BASE_URL;
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();

    const [code, setCode] = useState("");
    const [error, setError] = useState(false);
    const [loading, setLoading] = useState(false);
    const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
    const [selectedLanguage, setSelectedLanguage] = useState(null);

    // ✅ 쿼리 파라미터
    const urlFilterKey = (searchParams.get("filterKey") || "").trim();
    const urlCourseId = (searchParams.get("courseId") || "").trim();

    // ✅ 자동 실행 중복 방지(React18 StrictMode 대비)
    const autoValidatedRef = useRef(false);
    const courseResolvedRef = useRef(false);

    // ✅ fromIntro 가드: filterKey 또는 courseId가 있으면 허용
    useEffect(() => {
        if (!location.state?.fromIntro && !urlFilterKey && !urlCourseId) {
            navigate("/", { replace: true });
        }
    }, [location.state?.fromIntro, navigate, urlFilterKey, urlCourseId]);

    // 언어 초기화
    useEffect(() => {
        const currentLangCode = localStorage.getItem("language") || i18n.language || "ko";
        const lang = languages.find(l => l.code === currentLangCode) || languages[0];

        setSelectedLanguage({
            code: lang.code,
            name: i18n.t(lang.nameKey),
            Icon: lang.Icon
        });

        i18n.changeLanguage(lang.code);
    }, []);

    // ✅ validate 요청 (기존 로직 유지 + override 가능)
    const handleConfirm = useCallback(async (overrideFilterKey) => {
        const finalKey = (overrideFilterKey ?? code).trim();

        if (!finalKey) {
            setError(true);
            return;
        }

        setLoading(true);
        setError(false);
        try {
            const response = await fetch(`${baseUrl}/api/auth/validate`, {
                method: "POST",
                headers: {
                    "Accept": "application/json",
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({ filterKey: finalKey })
            });

            const data = await response.json();
            if (!data.success) throw new Error("Invalid code");

            if (data.sessionToken) {
                sessionStorage.setItem('lectureSessionToken', data.sessionToken);
            }

            navigate("/lecture", { state: { filterKey: data.filterKey } });
        } catch (error) {
            setError(true);
        } finally {
            setLoading(false);
        }
    }, [baseUrl, code, navigate]);

    // ✅ 1) filterKey로 들어오면: input 채우고 자동 검증
    useEffect(() => {
        if (!urlFilterKey) return;

        setCode(urlFilterKey);

        if (autoValidatedRef.current) return;
        autoValidatedRef.current = true;

        handleConfirm(urlFilterKey);
    }, [urlFilterKey, handleConfirm]);

    // NOTE: courseId 진입 플로우는 현재 미사용(보류).
    // 외부 링크는 auth_key(filterKey) 기반으로만 사용 중.
    // 필요 시 courseId → filterKey resolve 후 자동 인증 플로우를 다시 활성화할 수 있음.
    useEffect(() => {
        // filterKey가 있으면 이 로직은 실행할 필요 없음
        if (urlFilterKey) return;
        if (!urlCourseId) return;

        if (courseResolvedRef.current) return;
        courseResolvedRef.current = true;

        (async () => {
            try {
                setLoading(true);
                setError(false);

                const res = await fetch(
                    `${baseUrl}/api/auth/resolve?courseId=${encodeURIComponent(urlCourseId)}`,
                    { headers: { "Accept": "application/json" } }
                );
                const data = await res.json();

                if (!data?.success || !data?.filterKey) {
                    setError(true);
                    return;
                }

                const resolvedFilterKey = String(data.filterKey).trim();
                setCode(resolvedFilterKey);

                // ✅ 추천: URL 바꾸지 말고 바로 validate → lecture 이동
                handleConfirm(resolvedFilterKey);

                // (원하면 URL을 바꾸는 방식도 가능)
                // navigate(`/auth?filterKey=${encodeURIComponent(resolvedFilterKey)}`, { replace: true });

            } catch (e) {
                setError(true);
            } finally {
                setLoading(false);
            }
        })();
    }, [urlCourseId, urlFilterKey, baseUrl, handleConfirm]);

    const toggleLanguageDropdown = () => {
        setShowLanguageDropdown(!showLanguageDropdown);
    };

    const selectLanguage = (language) => {
        i18n.changeLanguage(language.code);
        localStorage.setItem("language", language.code);
        setSelectedLanguage({
            code: language.code,
            name: i18n.t(language.nameKey),
            Icon: language.Icon
        });
        setShowLanguageDropdown(false);
    };

    if (!selectedLanguage) return <AuthPageSkeleton />;

    return (
        <div className="auth-content-enter w-full min-h-[100dvh] bg-white flex flex-col items-center overflow-x-hidden">
            <div className="
        grid grid-cols-4 gap-1
        md:grid-cols-12 md:gap-2
        w-full px-6 h-full pt-safe-top mx-auto
        max-w-[640px] lg:max-w-[944px]
      ">
                <div className="
          col-span-4
          md:col-start-4 md:col-span-6
          flex flex-col items-start flex-1 pt-[56px] md:pt-[96px]
        ">
                    <div className="flex justify-start mb-2">
                        <ChatIcon className="w-[56px] h-[56px]" />
                    </div>

                    <div className="flex flex-col items-start gap-1 self-stretch mb-8">
                        <h1 className="text-[20px] sm:text-[22px] md:text-[24px] font-semibold leading-[140%] text-[#161819] whitespace-pre-line">
                            {t("auth.title")}
                        </h1>
                    </div>

                    <div className="w-full flex flex-col justify-center items-start self-stretch mb-2">
                        <input
                            type="text"
                            placeholder={t("auth.placeholder")}
                            value={code}
                            onChange={(e) => { setCode(e.target.value); setError(false); }}
                            className={`w-full h-12 px-3.5 py-2.5 rounded-lg border ${
                                error ? 'border-[#F04438]' : 'border-[#D3D6D9]'
                            } bg-white text-base font-normal leading-[150%] tracking-[-0.005em] text-[#333] placeholder:text-[#C4C8CC] focus:outline-none`}
                        />
                        {error && (
                            <div className="flex items-center self-stretch mt-1.5">
                                <p className="text-[#F04438] text-[14px] font-normal leading-[150%] tracking-[-0.005em]">
                                    {t("auth.error")}
                                </p>
                            </div>
                        )}
                    </div>

                    <button
                        className="lsp-primary flex h-12 pl-5 pr-[18px] justify-center items-center gap-1 self-stretch rounded-lg mb-2"
                        onClick={() => handleConfirm()}
                        disabled={loading || error || !code.trim()}
                    >
            <span
                className="text-[15px] font-medium leading-[150%] tracking-[-0.005em]"
            >
              {t("auth.button")}
            </span>
                    </button>

                    <div className="w-full flex justify-center relative">
                        <div className="flex items-center gap-2 cursor-pointer p-2" onClick={toggleLanguageDropdown}>
                            <selectedLanguage.Icon className="w-6 h-6 rounded-full border border-gray-300" />
                            <span className="text-[14px] text-gray-700">
                {selectedLanguage.name}
              </span>
                            <div className="w-6 h-6 -ml-1 rounded-md bg-[#FFFFFF03] flex items-center justify-center">
                                <DownArrow className="w-[18px] h-[18px]" />
                            </div>
                        </div>

                        {showLanguageDropdown && (
                            <div className="absolute top-full mt-2 w-[144px] bg-white rounded-md z-10 shadow-[0px_4px_10px_0px_#00000033]">
                                <div className="flex py-1 flex-col items-start gap-0.5">
                                    {languages.map((lang) => (
                                        <div
                                            key={lang.code}
                                            className="flex h-10 px-5 items-center gap-2 self-stretch active:bg-[#00000008] cursor-pointer"
                                            onClick={() => selectLanguage(lang)}
                                        >
                                            <lang.Icon className="w-6 h-6 rounded-full border border-gray-300" />
                                            <span className="text-[14px]">
                        {i18n.t(lang.nameKey)}
                      </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="w-full h-safe-bottom shrink-0 mt-auto col-span-full" />
            </div>
        </div>
    );
};

export default AuthPage;
